// Shared NC tax-foreclosure citation map — the single source of truth for
// statute/case references used by the risk-flag UI and the public education
// page (NCTaxForeclosureGuide.tsx).
//
// Mirrors the discipline of statusMeta.ts: one registry, a lookup helper, a
// graceful fallback. It also mirrors the statute/case index in
// docs/nc-tax-foreclosure/README.md §9 — keep the two in lockstep. When you
// add or change a citation here, update that index too.
//
// SOURCING RULE (from the docs guardrails): every entry below is pulled from
// the README's statute/case index. No statute number, holding, or reporter
// cite is invented. The one authority flagged as uncertain — County of Jackson
// v. Moor — carries its verbatim "verify reporter cite before relying" caveat.
//
// `citationFor(ref)` maps a backend `statuteRefs` string (the raw text the
// flag evaluator emits, e.g. "26 U.S.C. § 7425") to its entry, best-effort.
// It returns undefined for unknown refs so callers can render a neutral
// "basis not yet documented" chip rather than asserting an uncited claim.

export interface NcCitation {
  /** Short label for a chip, e.g. "G.S. 105-356" or "Mennonite v. Adams". */
  label: string;
  /** Full citation as it should read in prose / a tooltip. */
  citation: string;
  /** One-sentence plain-English summary of what the authority establishes. */
  summary: string;
  /** Optional link to the statute/opinion text. */
  url?: string;
}

// Base URLs for NC General Statutes + federal code chapters. The NCGA site
// serves per-chapter PDFs; we link to the chapter PDF rather than deep-linking
// a section (the section anchors are not stable).
const NCGA = "https://www.ncleg.gov/EnactedLegislation/Statutes/PDF/ByChapter";

/**
 * Master registry, keyed by a stable `citation_key` (the same keys the
 * implementation guide assigns to flag evaluators, e.g. `gs_105_356`,
 * `usc_26_7425`, `case_mennonite`). Add new authorities HERE.
 */
export const NC_STATUTES: Record<string, NcCitation> = {
  // ─── North Carolina statutes ──────────────────────────────────────
  gs_105_355: {
    label: "G.S. 105-355",
    citation: "N.C.G.S. § 105-355",
    summary: "Attachment and creation of the property-tax lien.",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_105_242: {
    label: "G.S. 105-242",
    citation: "N.C.G.S. § 105-242",
    summary:
      "Collection of NC taxes: a certificate of tax liability docketed with the clerk becomes a judgment lien on the debtor's real property in that county.",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_105_356: {
    label: "G.S. 105-356",
    citation: "N.C.G.S. § 105-356",
    summary:
      "The property-tax lien has super-priority over essentially all other liens regardless of recording date, subordinate only to senior NCDOR tax liens (first-in-time).",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_105_374: {
    label: "G.S. 105-374",
    citation: "N.C.G.S. § 105-374",
    summary:
      "Mortgage-style / judicial tax foreclosure: a civil action with necessary parties served under Rule 4; a commissioner sells free and clear only of interests included in the action.",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_105_375: {
    label: "G.S. 105-375",
    citation: "N.C.G.S. § 105-375",
    summary:
      "In rem tax foreclosure (strictly against the property): a docketed certificate of taxes, $250 admin-fee cap, 8% interest, and a sheriff's execution sale.",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_105_377: {
    label: "G.S. 105-377",
    citation: "N.C.G.S. § 105-377",
    summary:
      "Bars contests to title one year after the deed is recorded — EXCEPT judgments void for defective notice, which remain attackable.",
    url: `${NCGA}/Chapter_105.pdf`,
  },
  gs_1_339_25: {
    label: "G.S. 1-339.25",
    citation: "N.C.G.S. § 1-339.25",
    summary:
      "The 10-day upset-bid period for judicial/foreclosure sales; each qualifying upset bid restarts the clock.",
    url: `${NCGA}/Chapter_1.pdf`,
  },
  gs_1_339_64: {
    label: "G.S. 1-339.64",
    citation: "N.C.G.S. § 1-339.64",
    summary:
      "The 10-day upset-bid period for execution sales (the in rem path).",
    url: `${NCGA}/Chapter_1.pdf`,
  },
  gs_44_68_10: {
    label: "G.S. 44-68.10 et seq.",
    citation: "N.C.G.S. § 44-68.10 et seq. (Uniform Federal Lien Registration Act)",
    summary:
      "Federal tax liens on real property are filed with the CLERK OF SUPERIOR COURT — NOT the Register of Deeds. A buyer who searches only the Register of Deeds will miss them.",
    url: `${NCGA}/Chapter_44.pdf`,
  },
  gs_153a_140: {
    label: "G.S. 153A-140",
    citation: "N.C.G.S. § 153A-140",
    summary:
      "County nuisance-abatement (weed/trash) lien, carrying the same super-priority as property taxes; survives if the county was not joined.",
    url: `${NCGA}/Chapter_153A.pdf`,
  },
  gs_160a_193: {
    label: "G.S. 160A-193",
    citation: "N.C.G.S. § 160A-193",
    summary:
      "City nuisance-abatement lien, carrying property-tax super-priority; survives if the city was not joined.",
    url: `${NCGA}/Chapter_160A.pdf`,
  },
  gs_160a_233: {
    label: "G.S. 160A-233",
    citation: "N.C.G.S. § 160A-233",
    summary: "Municipal assessment-lien mechanics (special-assessment priority).",
    url: `${NCGA}/Chapter_160A.pdf`,
  },
  gs_160a_443: {
    label: "G.S. 160A-443",
    citation: "N.C.G.S. § 160A-443",
    summary:
      "Minimum-housing / demolition lien (special-assessment priority, junior to the tax lien). Can dwarf the tax lien and survives if the city was not joined.",
    url: `${NCGA}/Chapter_160A.pdf`,
  },
  gs_160d_1119: {
    label: "G.S. 160D-1119",
    citation: "N.C.G.S. § 160D-1119",
    summary:
      "Unsafe buildings condemned — the inspector affixes a notice of the structure's dangerous character to the building itself.",
    url: `${NCGA}/Chapter_160D.pdf`,
  },
  gs_160d_1126: {
    label: "G.S. 160D-1126",
    citation: "N.C.G.S. § 160D-1126",
    summary:
      "Inspection departments must keep complete records of permits, inspections, defects found and orders issued — the statutory basis for requesting code-enforcement records from a jurisdiction we do not ingest.",
    url: `${NCGA}/Chapter_160D.pdf`,
  },
  gs_160d_1203: {
    label: "G.S. 160D-1203",
    citation: "N.C.G.S. § 160D-1203",
    summary:
      "Minimum-housing orders to repair, close or demolish. The ordinance is recorded with the Register of Deeds and indexed against the owner in the grantor index, and the cost of demolition becomes a lien on the property.",
    url: `${NCGA}/Chapter_160D.pdf`,
  },
  gs_41_art4: {
    label: "Ch. 41, Article 4",
    citation: "N.C.G.S. Chapter 41, Article 4 (historically G.S. 41-10) — quiet title",
    summary:
      "The action to quiet title — one of the two routes to insurable title on a tax deed (ballpark $5,000–$6,000 and several months).",
    url: `${NCGA}/Chapter_41.pdf`,
  },

  // ─── Federal statutes ─────────────────────────────────────────────
  usc_26_6323: {
    label: "26 U.S.C. § 6323",
    citation: "26 U.S.C. § 6323 (incl. § 6323(b)(6))",
    summary:
      "Federal tax lien priority; § 6323(b)(6) ranks the local property-tax lien senior to the federal tax lien. Seniority does not cure the §7425 notice rule or the §2410(d) redemption right.",
    url: "https://www.law.cornell.edu/uscode/text/26/6323",
  },
  usc_26_7425: {
    label: "26 U.S.C. § 7425",
    citation: "26 U.S.C. § 7425",
    summary:
      "Written notice to the IRS at least 25 days before the sale is required to discharge a federal tax lien; an un-noticed federal lien survives the sale.",
    url: "https://www.law.cornell.edu/uscode/text/26/7425",
  },
  usc_28_2410d: {
    label: "28 U.S.C. § 2410(d)",
    citation: "28 U.S.C. § 2410(d)",
    summary:
      "Even with correct notice, the IRS retains a 120-day right of redemption after the sale — the buyer's title is not fully quiet until that window closes.",
    url: "https://www.law.cornell.edu/uscode/text/28/2410",
  },

  // ─── Cases ────────────────────────────────────────────────────────
  case_dixieland: {
    label: "Dixieland Realty v. Wysor",
    citation: "Dixieland Realty Co. v. Wysor, 272 N.C. 172 (1967)",
    summary:
      "Foreclosure of a senior lien extinguishes junior liens — but only if the junior lienholder was both joined as a party AND properly served.",
  },
  case_mullane: {
    label: "Mullane v. Central Hanover",
    citation: "Mullane v. Central Hanover Bank & Trust Co. (1950)",
    summary:
      "Notice must be reasonably calculated, under all the circumstances, to apprise interested parties of the action. The baseline due-process standard.",
  },
  case_mennonite: {
    label: "Mennonite Board of Missions v. Adams",
    citation: "Mennonite Board of Missions v. Adams, 462 U.S. 791 (1983)",
    summary:
      "A recorded mortgagee is entitled to actual notice by mail or personal service; publication alone is insufficient for a party whose identity and address are reasonably ascertainable.",
  },
  case_jones_flowers: {
    label: "Jones v. Flowers",
    citation: "Jones v. Flowers, 547 U.S. 220 (2006)",
    summary:
      "When mailed notice is returned undelivered, the government must take additional reasonable steps before selling, if practicable.",
  },
  case_osteen: {
    label: "Henderson County v. Osteen",
    citation: "Henderson County v. Osteen, 292 N.C. 692 (1977)",
    summary:
      "Statutory notice is indispensable; a curative statute cannot save a sale where notice was never given. The 105-377 one-year bar does not bar an attack on the sale.",
  },
  case_daye: {
    label: "County of Durham v. Daye",
    citation: "County of Durham v. Daye, 195 N.C. App. 527 (2009)",
    summary: "NC Court of Appeals application of the notice requirement to a tax foreclosure.",
  },
  case_moor: {
    label: "County of Jackson v. Moor",
    // Verbatim caveat, preserved exactly as the docs require it.
    citation:
      "County of Jackson v. Moor (N.C. App. 2014 — verify reporter cite before relying; likely unpublished)",
    summary:
      "Cited for the same notice-defect line. Confirm the citation and precedential status before relying on it in user-facing copy or a brief.",
  },
  case_ryan: {
    label: "Mecklenburg County v. Ryan",
    citation: "Mecklenburg County v. Ryan, 281 N.C. App. 646 (2022)",
    summary:
      "The county had the owner's email address but used publication anyway — held insufficient; the owner had in fact redeemed by phone.",
  },
  case_tyler_hennepin: {
    label: "Tyler v. Hennepin County",
    citation: "Tyler v. Hennepin County (2023)",
    summary:
      "A foreclosing government generally may not retain surplus equity beyond what it is owed (surplus-funds rights, relevant to former owners).",
  },
};

/**
 * Default fallback for an unknown citation key. Mirrors UNKNOWN_STATUS in
 * statusMeta.ts: always returns something renderable so a chip never crashes,
 * while flagging that the basis isn't yet documented (the docs guardrail:
 * "render a neutral 'basis not yet documented' chip and log it").
 */
export const UNKNOWN_CITATION: NcCitation = {
  label: "Basis not yet documented",
  citation: "Basis not yet documented",
  summary:
    "No statute or case is mapped for this reference yet — treat as engineering opinion, not law, and verify with NC counsel.",
};

/**
 * Look up a citation by its registry key (e.g. "gs_105_356"). Returns the
 * UNKNOWN_CITATION fallback for unknown keys so callers never null-check.
 */
export function ncStatute(key: string | null | undefined): NcCitation {
  if (!key) return UNKNOWN_CITATION;
  return NC_STATUTES[key] ?? UNKNOWN_CITATION;
}

// Maps the free-form `statuteRefs` strings a backend flag emits to a registry
// key. Best-effort + tolerant of whitespace and punctuation variants (the
// backend may emit "26 U.S.C. § 7425", "26 USC 7425", "G.S. 105-356", etc.).
//
// Built once at module load by normalizing each entry's own label + citation,
// plus a hand-curated set of common alternate spellings, into lookup keys.
function normalizeRef(ref: string): string {
  return ref
    .toLowerCase()
    .replace(/§/g, "")
    .replace(/u\.s\.c\.?/g, "usc")
    .replace(/n\.c\.g\.s\.?/g, "gs")
    .replace(/n\.c\.?\s*gen\.?\s*stat\.?/g, "gs")
    .replace(/g\.s\.?/g, "gs")
    .replace(/[^a-z0-9]/g, "");
}

// Hand-curated alternate spellings → registry key. Covers the exact strings
// the known flag types emit (federal_tax_lien → "26 U.S.C. § 7425" /
// "28 U.S.C. § 2410(d)"; title_insurability → quiet-title; subsequent_year →
// the foreclosure-method statutes).
const REF_ALIASES: Record<string, string> = {
  [normalizeRef("26 U.S.C. § 7425")]: "usc_26_7425",
  [normalizeRef("26 USC 7425")]: "usc_26_7425",
  [normalizeRef("28 U.S.C. § 2410(d)")]: "usc_28_2410d",
  [normalizeRef("28 U.S.C. § 2410")]: "usc_28_2410d",
  [normalizeRef("28 USC 2410")]: "usc_28_2410d",
  [normalizeRef("26 U.S.C. § 6323")]: "usc_26_6323",
  [normalizeRef("26 U.S.C. § 6323(b)(6)")]: "usc_26_6323",
  [normalizeRef("G.S. 105-242")]: "gs_105_242",
  [normalizeRef("G.S. 105-356")]: "gs_105_356",
  [normalizeRef("G.S. 105-355")]: "gs_105_355",
  [normalizeRef("G.S. 105-374")]: "gs_105_374",
  [normalizeRef("G.S. 105-375")]: "gs_105_375",
  [normalizeRef("G.S. 105-377")]: "gs_105_377",
  [normalizeRef("G.S. 1-339.25")]: "gs_1_339_25",
  [normalizeRef("G.S. 1-339.64")]: "gs_1_339_64",
  [normalizeRef("G.S. 44-68.10")]: "gs_44_68_10",
  [normalizeRef("G.S. 44-68.10 et seq.")]: "gs_44_68_10",
  [normalizeRef("G.S. 153A-140")]: "gs_153a_140",
  [normalizeRef("G.S. 160A-193")]: "gs_160a_193",
  [normalizeRef("G.S. 160A-233")]: "gs_160a_233",
  [normalizeRef("G.S. 160A-443")]: "gs_160a_443",
  [normalizeRef("G.S. 160D-1119")]: "gs_160d_1119",
  [normalizeRef("G.S. 160D-1126")]: "gs_160d_1126",
  [normalizeRef("G.S. 160D-1203")]: "gs_160d_1203",
  [normalizeRef("Chapter 41, Article 4")]: "gs_41_art4",
  [normalizeRef("Ch. 41, Article 4")]: "gs_41_art4",
  [normalizeRef("G.S. 41-10")]: "gs_41_art4",
};

// A normalized-label → key index over the registry itself, so a backend ref
// that matches an entry's own `label` resolves even without an explicit alias.
const LABEL_INDEX: Record<string, string> = Object.entries(NC_STATUTES).reduce(
  (acc, [key, entry]) => {
    acc[normalizeRef(entry.label)] = key;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Resolve a backend `statuteRefs` string (or a registry key) to its citation
 * entry. Returns undefined when nothing matches — callers render the neutral
 * "basis not yet documented" chip in that case.
 *
 * Accepts BOTH a raw ref ("26 U.S.C. § 7425") and a registry key
 * ("usc_26_7425"), so a flag can carry either form.
 *
 * @example
 * citationFor("26 U.S.C. § 7425")?.label  // "26 U.S.C. § 7425"
 * citationFor("gs_105_356")?.label        // "G.S. 105-356"
 * citationFor("made up")                  // undefined
 */
export function citationFor(ref: string | null | undefined): NcCitation | undefined {
  if (!ref) return undefined;
  // Exact registry key?
  if (NC_STATUTES[ref]) return NC_STATUTES[ref];
  const norm = normalizeRef(ref);
  if (!norm) return undefined;
  const aliasKey = REF_ALIASES[norm] ?? LABEL_INDEX[norm];
  if (aliasKey && NC_STATUTES[aliasKey]) return NC_STATUTES[aliasKey];
  return undefined;
}
